import { ReactNode, useEffect, useState } from 'react';
import { ProductLine, ReceiptData } from 'types';
import { Button } from './ui/Button';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS } from 'utils/constants';

const COUNTDOWN_SECONDS = 3;

interface Props {
  product: ProductLine;
  receiptData: ReceiptData | null;
  colorKey?: keyof typeof DISPOSITION_COLORS;
  title?: string;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

export const ConfirmCountdownScreen = ({
  product,
  receiptData,
  colorKey = 'CONFIRMING',
  title = 'CONFIRMED',
  onConfirm,
  onCancel,
  children,
}: Props) => {
  const [count, setCount] = useState(COUNTDOWN_SECONDS);
  const colors = DISPOSITION_COLORS[colorKey];

  useEffect(() => {
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onConfirm();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = (count / COUNTDOWN_SECONDS) * 100;

  return (
    <div
      className={cn(
        '-mx-4 -mb-6 flex min-h-full flex-col items-center px-6 pb-6 pt-6',
        colors.gradient
      )}
    >
      <div className="mb-4 text-center">
        <p className="text-5xl font-black uppercase tracking-widest text-white leading-none">
          {colors.icon} {title}
        </p>
        <p className="mt-2 text-base font-bold uppercase tracking-wide text-white/80">
          Heading to grading in {count}…
        </p>
      </div>

      <div className="w-full mb-6">
        <div className="h-3 w-full rounded-full bg-white/20 overflow-hidden">
          <div
            className="h-full rounded-full bg-white transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 w-full mb-6">
        <Button
          type="button"
          className={cn(
            'h-14 w-full rounded-2xl bg-white text-lg font-bold shadow-lg hover:bg-white/90',
            colors.buttonText
          )}
          onClick={onCancel}
        >
          Cancel
        </Button>
        {children}
      </div>

      {product.style_image_url && (
        <div className="mb-4 flex justify-center rounded-xl border border-white/20 bg-white/10 p-3 w-full">
          <img
            src={product.style_image_url}
            alt="Product"
            className="max-h-40 max-w-full rounded-lg object-contain"
          />
        </div>
      )}

      <div className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 space-y-2">
        <InfoRow
          label="Tracking"
          value={receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
        />
        <InfoRow label="Article" value={product.product_id} />
        <InfoRow label="Color" value={product.style_color} />
        <InfoRow label="Size" value={product.product_size} />
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex items-baseline gap-2">
    <span className="w-16 shrink-0 text-xs font-semibold text-white/70">{label}</span>
    <span className="text-sm font-semibold text-white break-all">{value ?? 'N/A'}</span>
  </div>
);
